// src/pages/subscription/BundleDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionBundle, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';
import { BundleFormDialog } from '@/features/subscription/BundleFormDialog';

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function statusVariant(status: string): 'success' | 'warning' | 'primary' | 'secondary' {
  switch (status) {
    case 'PRODUCT_BUNDLE_STATUS_DRAFT': return 'primary';
    case 'PRODUCT_BUNDLE_STATUS_ACTIVE': return 'success';
    case 'PRODUCT_BUNDLE_STATUS_SUSPENDED': return 'warning';
    case 'PRODUCT_BUNDLE_STATUS_ARCHIVED': return 'secondary';
    default: return 'secondary';
  }
}

export const BundleDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionBundle();
  const { t } = useI18n();
  const item = store.currentItem as any;
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (id) store.GetProductBundle(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleDelete = async () => {
    if (!id || !window.confirm(t('common.confirmDelete'))) return;
    try {
      await store.DeleteProductBundle(id);
      navigate('/subscriptions/bundles');
    } catch {
      // Error handled by store
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/bundles" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/bundles" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const items = item.items ?? [];

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/subscriptions/bundles" className="text-muted-foreground hover:text-foreground">Bundles</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
            <Badge variant={statusVariant(item.status)} size="sm">
              {formatLabel(item.status || '', 'PRODUCT_BUNDLE_STATUS_')}
            </Badge>
          </div>
          {item.tagline && <p className="text-sm text-muted-foreground mt-1">{String(item.tagline)}</p>}
        </div>
        <div className="flex gap-2">
          <Link to={`/subscriptions/bundles/${id}/subscribe`}><Button>Subscribe to Bundle</Button></Link>
          <Button variant="secondary" onClick={() => setShowEdit(true)}>{t('common.edit')}</Button>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      <BundleFormDialog
        open={showEdit}
        bundle={item ?? null}
        onClose={() => setShowEdit(false)}
        onSaved={() => { if (id) store.GetProductBundle(id); }}
      />

      {/* Basic Info */}
      <Card>
        <CardHeader><CardTitle>Basic Info</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.name ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.slug ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tagline</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.tagline ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2 whitespace-pre-wrap">{String(item.description ?? '--')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Status */}
      <Card>
        <CardHeader><CardTitle>Status</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm col-span-2">
                <Badge variant={statusVariant(item.status)} size="sm">
                  {formatLabel(item.status || '', 'PRODUCT_BUNDLE_STATUS_')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Public</dt>
              <dd className="text-sm col-span-2">
                <Badge variant={item.is_public ? 'success' : 'secondary'} size="sm">{item.is_public ? 'Yes' : 'No'}</Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Current Subscriptions</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.current_subscriptions ?? 0)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Max Subscriptions</dt>
              <dd className="text-sm text-foreground col-span-2">{item.max_subscriptions ? String(item.max_subscriptions) : 'Unlimited'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader><CardTitle>Pricing</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Price</dt>
              <dd className="text-sm text-foreground col-span-2">{formatMoney(item.bundle_price)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.currency ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Billing Cycle</dt>
              <dd className="text-sm text-foreground col-span-2">{formatLabel(item.billing_cycle || '', 'BILLING_CYCLE_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Discount Type</dt>
              <dd className="text-sm text-foreground col-span-2">{formatLabel(item.discount_type || '', 'DISCOUNT_TYPE_')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Discount Value</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.discount_value ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Trial Days</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.trial_period_days ?? 0)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Billing Mode */}
      <Card>
        <CardHeader><CardTitle>Billing Mode</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Mode</dt>
              <dd className="text-sm col-span-2">
                <Badge variant="primary" size="sm">{formatLabel(item.billing_mode || '', 'PRODUCT_BUNDLE_BILLING_MODE_')}</Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Explanation</dt>
              <dd className="text-sm text-muted-foreground col-span-2">
                {item.billing_mode === 'PRODUCT_BUNDLE_BILLING_MODE_SINGLE'
                  ? 'All products in this bundle are billed as a single charge.'
                  : 'Products in this bundle are billed with linked but separate charges.'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Bundle Items */}
      <Card>
        <CardHeader><CardTitle>Bundle Items</CardTitle></CardHeader>
        <CardContent>
          {items.length > 0 ? (
            <Table striped>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Core</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((bundleItem: any, i: number) => (
                  <TableRow key={bundleItem.entity?.id || bundleItem.id || i}>
                    <TableCell>{String(bundleItem.product_name ?? bundleItem.product_id ?? '--')}</TableCell>
                    <TableCell>{String(bundleItem.plan_name ?? bundleItem.plan_id ?? '--')}</TableCell>
                    <TableCell>
                      <Badge variant={bundleItem.required ? 'warning' : 'secondary'} size="sm">{bundleItem.required ? 'Yes' : 'No'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={bundleItem.core ? 'primary' : 'secondary'} size="sm">{bundleItem.core ? 'Yes' : 'No'}</Badge>
                    </TableCell>
                    <TableCell>{String(bundleItem.quantity ?? 1)}</TableCell>
                    <TableCell>{String(bundleItem.order ?? bundleItem.sort_order ?? i + 1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No items in this bundle</p>
          )}
        </CardContent>
      </Card>

      {/* External */}
      <Card>
        <CardHeader><CardTitle>External Integrations</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Stripe Product ID</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.stripe_product_id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Stripe Price ID</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.stripe_price_id ?? '--')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader><CardTitle>Timestamps</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Published</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.published_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Archived</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.archived_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default BundleDetailPage;
